import { z } from "zod";
const identifier = z.string().regex(/^[A-Za-z0-9_-]{1,128}$/);
const revision = z.string().regex(/^edit-[a-f0-9]{64}$/);
export const InvoiceValidationStateSchema = z.enum([
  "QUEUED",
  "RUNNING",
  "VALID",
  "INVALID",
  "FAILED",
]);
export const InvoiceValidationActionSchema = z
  .object({
    action: z.enum(["START", "RETRY"]),
    editRevision: revision,
    intentId: z.string().uuid().nullable(),
    expectedRunId: z.string().uuid().nullable(),
  })
  .strict()
  .superRefine((value, context) => {
    if (
      (value.intentId === null) !== (value.expectedRunId === null) ||
      (value.action === "RETRY" && value.intentId === null)
    )
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["intentId"],
        message: "Review the current validation run.",
      });
  });
export const InvoiceValidationAttemptSchema = z
  .object({
    tenantId: identifier,
    actorId: z.string().min(1).max(200),
    attemptKey: z.string().uuid(),
    documentId: identifier,
    action: z.enum(["START", "RETRY"]),
    editRevision: revision,
    intentId: z.string().uuid(),
    expectedRunId: z.string().uuid().nullable(),
    runId: z.string().uuid(),
    recordedAt: z.string().datetime(),
  })
  .strict()
  .superRefine((value, context) => {
    if (
      (value.action === "START" &&
        value.expectedRunId !== null &&
        value.runId !== value.expectedRunId) ||
      (value.action === "RETRY" &&
        (value.expectedRunId === null || value.runId === value.expectedRunId))
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Unconfirmed validation request result.",
      });
    }
  });
export const InvoiceValidationViewSchema = z
  .object({
    tenantId: identifier,
    documentId: identifier,
    editRevision: revision,
    intent: z
      .object({
        id: z.string().uuid(),
        sourceRevision: revision,
        runId: z.string().uuid(),
        state: InvoiceValidationStateSchema,
        reason: z
          .enum([
            "AWAITING_DISPATCH",
            "DISPATCH_FAILED",
            "EXECUTION_FAILED",
            "INTERRUPTED",
          ])
          .nullable(),
        requestedAt: z.string().datetime(),
        startedAt: z.string().datetime().nullable(),
        finishedAt: z.string().datetime().nullable(),
        errors: z.array(z.string().max(2000)).max(2000),
        warnings: z.array(z.string().max(2000)).max(2000),
      })
      .strict()
      .nullable(),
    canStart: z.boolean(),
    canRetry: z.boolean(),
    blockedReason: z
      .enum([
        "INCOMPLETE",
        "NUMBERING_REQUIRED",
        "DOCUMENT_LOCKED",
        "NO_INTENT",
      ])
      .nullable(),
  })
  .strict()
  .superRefine((value, context) => {
    const invalid =
      (value.canStart && value.canRetry) ||
      (value.canRetry && value.intent?.state !== "FAILED") ||
      (value.canStart &&
        value.intent !== null &&
        value.intent.state !== "QUEUED") ||
      (value.intent?.state === "VALID" && value.intent.errors.length > 0);
    if (invalid)
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Unconfirmed validation state.",
      });
  });
export type InvoiceValidationState = z.infer<
  typeof InvoiceValidationStateSchema
>;
export type InvoiceValidationAction = z.infer<
  typeof InvoiceValidationActionSchema
>;
export type InvoiceValidationAttempt = z.infer<
  typeof InvoiceValidationAttemptSchema
>;
export type InvoiceValidationView = z.infer<typeof InvoiceValidationViewSchema>;
